import React, { createContext, useContext, useState, useEffect, useCallback, type PropsWithChildren } from 'react';
import { api } from '../api/client';
import type { UserMapping, PhoneCheckResult, RequestAccessData } from '../types';

interface AuthContextType {
  user: UserMapping | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  checkPhone: (phone: string) => Promise<PhoneCheckResult>;
  login: (data: {
    contactId: number;
    companyId: number;
    phone: string;
    fullName?: string;
    companyInn?: string;
  }) => Promise<void>;
  requestAccess: (data: RequestAccessData) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Провайдер авторизации
 * Проверяет сессию при монтировании через GET /api/auth/me
 */
export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<UserMapping | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Проверка сессии при загрузке
  useEffect(() => {
    async function checkSession() {
      try {
        const userData = await api.auth.me();
        setUser(userData);
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    checkSession();
  }, []);

  const checkPhone = useCallback(async (phone: string): Promise<PhoneCheckResult> => {
    return api.auth.checkPhone(phone);
  }, []);

  const login = useCallback(async (data: {
    contactId: number;
    companyId: number;
    phone: string;
    fullName?: string;
    companyInn?: string;
  }) => {
    const userData = await api.auth.login(data);
    setUser(userData);
  }, []);

  const requestAccess = useCallback(async (data: RequestAccessData) => {
    return api.auth.requestAccess(data);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // Даже если сервер недоступен — всё равно выходим локально
    }
    setUser(null);
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    checkPhone,
    login,
    requestAccess,
    logout,
  };

  return React.createElement(AuthContext.Provider, { value }, children);
}

/**
 * Хук для доступа к авторизации
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
